import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const RiskPredictions = ({ risks, onRiskUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for risk changes
  React.useEffect(() => {
    if (!projectId) return;

    const riskChannel = moduleRealtimeService?.subscribeToRiskChanges(
      projectId,
      (payload) => {
        console.log('Risk change detected:', payload?.event);
        
        // Trigger risk refresh when data changes
        if (onRiskUpdate) {
          onRiskUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(riskChannel);
    };
  }, [projectId, onRiskUpdate]);

  const getRiskColor = (score) => {
    if (score >= 7) return 'var(--color-error)';
    if (score >= 4) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getRiskIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'schedule':
        return 'Clock';
      case 'budget':
        return 'DollarSign';
      case 'quality':
        return 'Shield';
      case 'safety':
        return 'AlertTriangle';
      case 'resource':
        return 'Users';
      default:
        return 'AlertCircle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'var(--color-error)';
      case 'mitigated':
        return 'var(--color-success)';
      case 'closed':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-warning)';
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h2 className="text-base font-semibold text-foreground">Risk Predictions</h2>
        <span className="text-xs text-muted-foreground">{risks?.length || 0} risks</span>
      </div>

      {!risks || risks?.length === 0 ? (
        <p className="text-muted-foreground text-sm">No risks identified</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex gap-3" style={{ width: 'max-content' }}>
            {risks?.map((risk) => (
              <div
                key={risk?.id}
                className="p-2.5 rounded-lg border transition-colors hover:bg-muted/50 flex-shrink-0"
                style={{ 
                  borderColor: getRiskColor(risk?.riskScore),
                  width: 'calc(25% - 0.75rem)',
                  minWidth: '280px'
                }}
              >
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Icon name={getRiskIcon(risk?.riskCategory)} size={14} color={getRiskColor(risk?.riskScore)} />
                    <span className="text-xs font-semibold text-muted-foreground uppercase">{risk?.riskCategory}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div
                      className="px-1.5 py-0.5 rounded text-xs font-bold"
                      style={{ backgroundColor: `${getRiskColor(risk?.riskScore)}20`, color: getRiskColor(risk?.riskScore) }}
                    >
                      Score: {risk?.riskScore}
                    </div>
                    <div
                      className="px-1.5 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${getStatusColor(risk?.status)}20`, color: getStatusColor(risk?.status) }}
                    >
                      {risk?.status?.charAt(0)?.toUpperCase() + risk?.status?.slice(1)}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-foreground mb-1.5 leading-relaxed">{risk?.riskDescription}</p>

                <div className="flex items-center gap-2.5 mb-1.5">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">Probability:</span>
                    <span className="text-xs font-medium text-foreground capitalize">{risk?.probability}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">Impact:</span>
                    <span className="text-xs font-medium text-foreground capitalize">{risk?.impact}</span>
                  </div>
                </div>

                {risk?.mitigationPlan && (
                  <div className="mt-1.5 p-1.5 bg-muted/50 rounded">
                    <div className="flex items-start gap-1">
                      <Icon name="Lightbulb" size={12} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground mb-0.5">Mitigation Plan:</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{risk?.mitigationPlan}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskPredictions;